import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, Printer } from "lucide-react";

export default function Constancias() {
  const { user } = useAuth();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [tipoConstancia, setTipoConstancia] = useState("ESTANDAR"); // ESTANDAR | PROMEDIO | BAJA | BECA
  const [alcance, setAlcance] = useState("INDIVIDUAL"); // INDIVIDUAL | GRUPO
  const [grupos, setGrupos] = useState([]);
  const [grupoId, setGrupoId] = useState("");
  const [alumnos, setAlumnos] = useState([]);
  const [alumnoId, setAlumnoId] = useState("");
  const [ciclo, setCiclo] = useState("");
  const [lugar, setLugar] = useState("Ciudad y Puerto de Tampico, Tamaulipas");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        setPlanteles(data);
        if (data.length) setPlantelId(data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!plantelId) return;
    (async () => {
      try {
        const { data } = await api.get("/grupos", { params: { plantel_id: plantelId } });
        setGrupos(data);
        if (data.length && !ciclo) setCiclo(data[0].ciclo);
      } catch { /* ignore */ }
    })();
  }, [plantelId, ciclo]);

  useEffect(() => {
    if (!grupoId) { setAlumnos([]); return; }
    (async () => {
      try {
        // Si es constancia de BAJA, incluir alumnos dados de baja y filtrar solo esos.
        const params = { grupo_id: grupoId };
        if (tipoConstancia === "BAJA") params.include_bajas = true;
        const { data } = await api.get("/alumnos", { params });
        const filtered = tipoConstancia === "BAJA" ? data.filter((a) => a.activo === false) : data;
        setAlumnos(filtered);
        // Si el alumno seleccionado ya no es válido para este tipo, limpiarlo
        if (alumnoId && !filtered.some((a) => a.id === alumnoId)) setAlumnoId("");
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupoId, tipoConstancia]);

  const buildUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("plantel_id", plantelId);
    params.set("alcance", alcance);
    params.set("tipo", tipoConstancia);
    if (grupoId) params.set("grupo_id", grupoId);
    if (alumnoId) params.set("alumno_id", alumnoId);
    if (ciclo) params.set("ciclo", ciclo);
    if (lugar) params.set("lugar", lugar);
    if (fecha) params.set("fecha", fecha);
    return `/constancias/print?${params.toString()}`;
  }, [plantelId, alcance, tipoConstancia, grupoId, alumnoId, ciclo, lugar, fecha]);

  const canGenerate = !!plantelId && !!ciclo && (
    (alcance === "GRUPO" && grupoId) ||
    (alcance === "INDIVIDUAL" && alumnoId)
  );

  return (
    <AppLayout>
      <div className="max-w-3xl">
        <div className="mb-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Documentos imprimibles</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Constancia de estudios</h1>
          <p className="text-sm text-[#545454] mt-1">Genera una constancia oficial lista para firma del Director(a). Puede emitirse individualmente o para todo un grupo.</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded p-5 space-y-4">
          {["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role) && planteles.length > 1 && (
            <Field label="Plantel">
              <Select value={plantelId} onValueChange={setPlantelId}>
                <SelectTrigger data-testid="const-plantel"><SelectValue /></SelectTrigger>
                <SelectContent>{planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}

          <Field label="Tipo de constancia">
            <div className="flex flex-wrap gap-2">
              {[
                ["ESTANDAR", "Simple"],
                ["PROMEDIO", "Con promedio general"],
                ["BAJA", "De baja"],
                ["BECA", "Trámite de beca"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`const-tipo-${v.toLowerCase()}`}
                  onClick={() => setTipoConstancia(v)}
                  className={`px-4 py-2 rounded border text-sm transition-colors ${tipoConstancia === v ? "bg-[#10312B] text-white border-[#10312B]" : "bg-white text-[#10312B] border-[#D1D5DB] hover:border-[#10312B]"}`}
                >
                  {l}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#6B7280] mt-1">
              {tipoConstancia === "ESTANDAR" && "Constancia simple: alumno inscrito de forma regular en el ciclo escolar."}
              {tipoConstancia === "PROMEDIO" && "Incluye el promedio general con los trimestres capturados en la boleta."}
              {tipoConstancia === "BAJA" && "Declara la baja del alumno a partir de la fecha registrada en su estatus."}
              {tipoConstancia === "BECA" && "Formato específico para trámite de beca (incluye promedio y menciona el propósito)."}
            </p>
          </Field>

          <Field label="Alcance">
            <div className="flex gap-2">
              {[["INDIVIDUAL", "Un alumno"], ["GRUPO", "Todo un grupo"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`const-alcance-${v.toLowerCase()}`}
                  onClick={() => { setAlcance(v); setAlumnoId(""); }}
                  className={`px-4 py-2 rounded border text-sm transition-colors ${alcance === v ? "bg-[#10312B] text-white border-[#10312B]" : "bg-white text-[#10312B] border-[#D1D5DB] hover:border-[#10312B]"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Grupo">
            <Select value={grupoId} onValueChange={(v) => { setGrupoId(v); setAlumnoId(""); }}>
              <SelectTrigger data-testid="const-grupo"><SelectValue placeholder="Selecciona grupo…" /></SelectTrigger>
              <SelectContent>
                {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{`${g.grado}° "${g.letra}" · Ciclo ${g.ciclo}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {alcance === "INDIVIDUAL" && (
            <Field label={tipoConstancia === "BAJA" ? "Alumno dado de baja" : "Alumno"}>
              <Select value={alumnoId} onValueChange={setAlumnoId}>
                <SelectTrigger data-testid="const-alumno">
                  <SelectValue placeholder={tipoConstancia === "BAJA" && grupoId && alumnos.length === 0 ? "Sin bajas en este grupo" : "Selecciona alumno…"} />
                </SelectTrigger>
                <SelectContent>
                  {alumnos.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {[a.nombre, a.apellido_paterno, a.apellido_materno].filter(Boolean).join(" ")}
                      {tipoConstancia === "BAJA" && a.fecha_baja ? ` · ${new Date(a.fecha_baja).toLocaleDateString("es-MX")}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tipoConstancia === "BAJA" && grupoId && alumnos.length === 0 && (
                <p className="text-[11px] text-[#9F2241] mt-1">
                  No hay alumnos dados de baja en este grupo. Da de baja al alumno primero desde <strong>Bajas</strong>.
                </p>
              )}
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Ciclo escolar">
              <Input value={ciclo} onChange={(e) => setCiclo(e.target.value)} placeholder="2026-2027" data-testid="const-ciclo" />
            </Field>
            <Field label="Fecha de expedición">
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="const-fecha" />
            </Field>
          </div>

          <Field label="Ciudad / Lugar">
            <Input value={lugar} onChange={(e) => setLugar(e.target.value)} data-testid="const-lugar" placeholder="Ciudad y Puerto de Tampico, Tamaulipas" />
          </Field>

          <div className="pt-2 flex items-center justify-between border-t border-[#F3F4F6]">
            <div className="text-xs text-[#6B7280] inline-flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> {alcance === "GRUPO" ? `${alumnos.length} alumno(s) en el grupo` : (alumnoId ? "1 alumno seleccionado" : "Selecciona alumno")}
            </div>
            <Button
              data-testid="btn-generar-constancias"
              disabled={!canGenerate}
              onClick={() => window.open(buildUrl, "_blank")}
              className="bg-[#235B4E] hover:bg-[#1D4A40] text-white"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Generar constancia{alcance === "GRUPO" ? "s" : ""}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1 block">{label}</label>
      {children}
    </div>
  );
}
